import { zodResolver } from '@hookform/resolvers/zod'
import { HandCoinsIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetVisitorParkingSettings } from '@/api/visitor-parking/get-visitor-parking-settings'
import { useUpdateVisitorParkingSettings } from '@/api/visitor-parking/update-visitor-parking-settings'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Typography } from '@/components/ui/typography'
import { toastFactory } from '@/factories'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils/error'

import * as S from './service-fee-modal.styled'
import {
  makeServiceFeeFormSchema,
  ServiceFeeFormData,
  ServiceFeeModalProps,
} from './service-fee-modal.types'
import {
  composeApiPricing,
  composePricingFormValues,
  getDefaultPricing,
} from './service-fee-modal.utils'
import { ServiceFeeRow } from './service-fee-row'

export const ServiceFeeModal = ({ onClose }: ServiceFeeModalProps) => {
  const { t } = useTranslation(['parking-management'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id
  const { data, isLoading } = useGetVisitorParkingSettings({ buildingId: buildingId ?? 0 })
  const { mutateAsync: updateVisitorParkingSettings, isPending } = useUpdateVisitorParkingSettings()

  const schema = useMemo(() => makeServiceFeeFormSchema(t), [t])

  const form = useForm<ServiceFeeFormData>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
    // Note: Update form values when data is fetched from the API
    values: {
      pricing: data?.pricing ? composePricingFormValues(data.pricing) : getDefaultPricing(),
    },
  })
  const { fields } = useFieldArray({
    control: form.control,
    name: 'pricing',
  })
  const hasPriceIncreaseError = !!form.formState.errors.pricing

  const onSubmit = async (formData: ServiceFeeFormData) => {
    if (!buildingId) return

    try {
      await updateVisitorParkingSettings({
        buildingId,
        pricing: composeApiPricing(formData.pricing),
      })
      toastFactory.success(t('parking-management:serviceFeeModal.toastSuccess'))
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  return (
    <Modal
      size="small"
      footer={
        <>
          <Button use="secondary" onClick={onClose}>
            {t('parking-management:serviceFeeModal.cancel')}
          </Button>
          <Button disabled={isLoading} loading={isPending} onClick={form.handleSubmit(onSubmit)}>
            {t('parking-management:serviceFeeModal.save')}
          </Button>
        </>
      }
      headerIcon={HandCoinsIcon}
      title={t('parking-management:serviceFeeModal.title')}
      closeOnOverlayClick={false}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <S.Body>
          {hasPriceIncreaseError && (
            <Alert
              type="error"
              title={t('parking-management:serviceFeeModal.invalidPricingTitle')}
              message={t('parking-management:serviceFeeModal.invalidPricingMessage')}
            />
          )}
          <S.Table>
            <S.TableHead>
              <tr>
                <Typography as="th" size="text-medium" weight="regular">
                  {t('parking-management:serviceFeeModal.columns.hours')}
                </Typography>
                <Typography as="th" size="text-medium" weight="regular">
                  {t('parking-management:serviceFeeModal.columns.fee')}
                </Typography>
              </tr>
            </S.TableHead>
            <tbody>
              {fields.map((field, index) => (
                <ServiceFeeRow
                  key={field.id}
                  index={index}
                  duration={field.duration}
                  isLoading={isLoading}
                  // Note: Show switch and tooltip for all except the first and last step
                  showSwitchAndTooltip={index !== 0 && index !== fields.length - 1}
                />
              ))}
            </tbody>
          </S.Table>
        </S.Body>
      </FormProvider>
    </Modal>
  )
}
